import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAccessToken } from "../lib/api";
import { assistantMutations } from "../mutations";
import { assistantQueries } from "../queries";
import type { Message, Thread } from "../types";

type UseAssistantChatParams = {
  analysisId: string;
};

const TEMP_ASSISTANT_MESSAGE_PREFIX = "temp-assist-";
const TEMP_USER_MESSAGE_PREFIX = "temp-msg-";

function createMessagePreview(content: string): string {
  return content.slice(0, 55) + (content.length > 55 ? "..." : "");
}

function createCurrentTimeLabel(): string {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function createNetworkErrorMessage(message?: string): string {
  return (
    message ||
    "We can't respond to you right now due to network issues. Please try again."
  );
}

function isTemporaryMessage(message: Message): boolean {
  return (
    message.id.startsWith(TEMP_ASSISTANT_MESSAGE_PREFIX) ||
    message.id.startsWith(TEMP_USER_MESSAGE_PREFIX)
  );
}

function hasMatchingPersistedMessage(
  messages: Message[],
  temporaryMessage: Message,
): boolean {
  const temporaryContent = temporaryMessage.content.trim();
  return messages.some(
    (message) =>
      message.role === temporaryMessage.role &&
      message.content.trim() === temporaryContent,
  );
}

function mergeWithOptimisticMessages(
  persistedMessages: Message[],
  currentMessages: Message[],
): Message[] {
  const optimisticMessages = currentMessages.filter(
    (message) =>
      isTemporaryMessage(message) &&
      !hasMatchingPersistedMessage(persistedMessages, message),
  );

  return [...persistedMessages, ...optimisticMessages];
}

export function useAssistantChat({ analysisId }: UseAssistantChatParams) {
  const queryClient = useQueryClient();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [currentMessages, setCurrentMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showThreadList, setShowThreadList] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [tempTitle, setTempTitle] = useState("");
  const [streamingHasError, setStreamingHasError] = useState(false);
  const [justSavedId, setJustSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const previousAnalysisIdRef = useRef(analysisId);
  const conversationsDataRef = useRef<Thread[] | undefined>(undefined);
  const pendingSendConversationIdRef = useRef<string | null>(null);

  const conversationsQuery = useQuery(
    assistantQueries.conversations.list(analysisId),
  );

  const resolvedActiveThreadId =
    activeThreadId && threads.some((thread) => thread.id === activeThreadId)
      ? activeThreadId
      : (threads[0]?.id ?? null);

  const activeConversationId =
    resolvedActiveThreadId && !resolvedActiveThreadId.startsWith("temp-")
      ? resolvedActiveThreadId
      : "";

  const suggestionsQuery = useQuery(
    assistantQueries.suggestions.list(
      activeConversationId,
      currentMessages.length > 0 && !isSending,
    ),
  );

  useEffect(() => {
    if (previousAnalysisIdRef.current !== analysisId) {
      previousAnalysisIdRef.current = analysisId;
      conversationsDataRef.current = undefined;
      setThreads([]);
      setActiveThreadId(null);
      setCurrentMessages([]);
      setInput("");
      setIsTyping(false);
      setIsSending(false);
      setShowThreadList(false);
      setEditingTitleId(null);
      setTempTitle("");
      setStreamingHasError(false);
      setJustSavedId(null);
      setError(null);
    }
  }, [analysisId]);

  useEffect(() => {
    if (conversationsDataRef.current !== conversationsQuery.data) {
      conversationsDataRef.current = conversationsQuery.data;
      setThreads(conversationsQuery.data ?? []);
    }
  }, [conversationsQuery.data]);

  useEffect(() => {
    if (
      !activeConversationId ||
      pendingSendConversationIdRef.current === activeConversationId
    ) {
      return;
    }

    void queryClient
      .fetchQuery(assistantQueries.conversations.detail(activeConversationId))
      .then((messages) => {
        setCurrentMessages(messages);
        const lastMessage = messages.at(-1);
        if (!lastMessage) return;

        setThreads((prev) =>
          prev.map((thread) =>
            thread.id === activeConversationId
              ? {
                  ...thread,
                  lastMessage: createMessagePreview(lastMessage.content),
                }
              : thread,
          ),
        );
      });
  }, [activeConversationId, queryClient]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError(null);
    }, 2000);

    return () => clearTimeout(timer);
  }, [error]);

  const updateThreadPreviewAndMoveToTop = useCallback(
    (conversationId: string, newLastMessage: string) => {
      setThreads((prev) => {
        const index = prev.findIndex((thread) => thread.id === conversationId);
        if (index === -1) return prev;
        const currentThread = prev[index];
        if (!currentThread) return prev;

        const updatedThread: Thread = {
          ...currentThread,
          lastMessage: newLastMessage,
          timestamp: "Just now",
        };

        const newList = [...prev];
        newList.splice(index, 1);
        newList.unshift(updatedThread);

        return newList;
      });
    },
    [],
  );

  const refreshConversations = useCallback(async () => {
    if (!analysisId) return;

    await queryClient.invalidateQueries({
      queryKey: assistantQueries.conversations.lists().queryKey,
    });
  }, [analysisId, queryClient]);

  const loadMessages = useCallback(
    async (
      conversationId: string,
      options: { preserveOptimistic?: boolean } = {},
    ) => {
      const messages = await queryClient.fetchQuery(
        assistantQueries.conversations.detail(conversationId),
      );
      setCurrentMessages((current) =>
        options.preserveOptimistic
          ? mergeWithOptimisticMessages(messages, current)
          : messages,
      );
      return messages;
    },
    [queryClient],
  );

  const sendPrompt = useCallback(async () => {
    if (!analysisId || !input.trim() || isTyping) return;

    const prompt = input.trim();
    const tempMsgId = `temp-msg-${Date.now()}`;
    const tempAssistId = `temp-assist-${Date.now()}`;
    const isNew =
      !resolvedActiveThreadId || resolvedActiveThreadId.startsWith("temp-");
    const previousThreadId = resolvedActiveThreadId;
    const abortController = new AbortController();
    const userTemp: Message = {
      id: tempMsgId,
      role: "user",
      content: prompt,
      timestamp: createCurrentTimeLabel(),
    };

    setCurrentMessages((prev) => [...prev, userTemp]);
    setInput("");
    setIsTyping(true);
    setIsSending(true);

    try {
      const accessToken = await getAccessToken();
      let conversationId = resolvedActiveThreadId;

      if (isNew) {
        const conversation = await assistantMutations.createConversation({
          analysisId,
          accessToken,
        });
        conversationId = conversation.conversation_id;
        const realThread: Thread = {
          id: conversation.conversation_id,
          name: conversation.title || "Analysis Chat",
          lastMessage: "No messages yet",
          timestamp: "Just now",
        };

        pendingSendConversationIdRef.current = conversation.conversation_id;
        setActiveThreadId(conversation.conversation_id);
        setThreads((prev) => {
          const withoutTemp = previousThreadId
            ? prev.filter((thread) => thread.id !== previousThreadId)
            : prev;
          return [realThread, ...withoutTemp];
        });
      }

      if (!conversationId) {
        throw new Error("No conversation selected");
      }

      pendingSendConversationIdRef.current = conversationId;

      await assistantMutations.sendMessage({
        conversationId,
        prompt,
        accessToken,
        abortController,
        callbacks: {
          onChunk: (delta) => {
            setStreamingHasError(false);
            setCurrentMessages((prev) => {
              const exists = prev.some(
                (message) => message.id === tempAssistId,
              );
              if (!exists) {
                setIsTyping(false);
                return [
                  ...prev,
                  {
                    id: tempAssistId,
                    role: "assistant",
                    content: delta,
                    timestamp: createCurrentTimeLabel(),
                  },
                ];
              }

              return prev.map((message) =>
                message.id === tempAssistId
                  ? { ...message, content: message.content + delta }
                  : message,
              );
            });
          },
          onDone: async () => {
            setIsTyping(false);
            updateThreadPreviewAndMoveToTop(
              conversationId,
              createMessagePreview(prompt),
            );
            await refreshConversations();
            await loadMessages(conversationId, { preserveOptimistic: true });
            await queryClient.invalidateQueries({
              queryKey:
                assistantQueries.suggestions.list(conversationId).queryKey,
            });
          },
          onError: (message) => {
            setStreamingHasError(true);
            setError(createNetworkErrorMessage(message));
          },
        },
      });
    } catch (err) {
      console.error("Chat error:", err);
      setCurrentMessages((prev) =>
        prev.filter(
          (message) => message.id !== tempMsgId && message.id !== tempAssistId,
        ),
      );
      setError(createNetworkErrorMessage());
    } finally {
      pendingSendConversationIdRef.current = null;
      setIsTyping(false);
      setIsSending(false);
    }
  }, [
    analysisId,
    input,
    isTyping,
    loadMessages,
    queryClient,
    refreshConversations,
    resolvedActiveThreadId,
    updateThreadPreviewAndMoveToTop,
  ]);

  const createNewThread = useCallback(() => {
    const tempId = `temp-${Date.now()}`;
    const newThread: Thread = {
      id: tempId,
      name: `New Thread ${threads.length + 1}`,
      lastMessage: "Start a new conversation...",
      timestamp: "Just now",
    };
    setThreads((prev) => [newThread, ...prev]);
    setActiveThreadId(tempId);
    setCurrentMessages([]);
    setShowThreadList(false);
  }, [threads.length]);

  const startEditingTitle = useCallback(
    (threadId: string, currentName: string) => {
      setEditingTitleId(threadId);
      setTempTitle(currentName);
    },
    [],
  );

  const saveTitle = useCallback(async () => {
    if (!editingTitleId) return;

    const trimmed = tempTitle.trim();
    if (!trimmed) {
      setEditingTitleId(null);
      return;
    }

    if (!editingTitleId.startsWith("temp-")) {
      try {
        await assistantMutations.updateConversationTitle({
          conversationId: editingTitleId,
          title: trimmed,
        });
      } catch (err) {
        console.error("Failed to update conversation title:", err);
        setError("We couldn't save that thread title. Please try again.");
        return;
      }
    }

    setThreads((prev) =>
      prev.map((thread) =>
        thread.id === editingTitleId ? { ...thread, name: trimmed } : thread,
      ),
    );
    setEditingTitleId(null);
    setJustSavedId(editingTitleId);
    setTimeout(() => {
      setJustSavedId(null);
    }, 1800);
    await refreshConversations();
  }, [editingTitleId, refreshConversations, tempTitle]);

  const cancelEditing = useCallback(() => {
    setEditingTitleId(null);
  }, []);

  return {
    activeThreadId: resolvedActiveThreadId,
    cancelEditing,
    createNewThread,
    currentMessages,
    editingTitleId,
    error,
    hasMessages: currentMessages.length > 0,
    input,
    isLoadingThreads: conversationsQuery.isLoading,
    isTyping,
    justSavedId,
    saveTitle,
    sendPrompt,
    setActiveThreadId,
    setInput,
    setShowThreadList,
    setTempTitle,
    showThreadList,
    startEditingTitle,
    streamingHasError,
    suggestions: suggestionsQuery.data ?? [],
    isLoadingSuggestions: suggestionsQuery.isFetching,
    refreshSuggestions: suggestionsQuery.refetch,
    tempTitle,
    threads,
  };
}
